/**
 * Donate page - main donation interface
 */

import { useState, useEffect } from 'react';
import { useCompany } from '@/contexts/CompanyContext';
import { useCart } from '@/contexts/CartContext';
import { getDonationTypes } from '@/services/api/donationTypeService';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import {
  WelcomeMessage,
  DonationTypeCard,
  Cart,
  AmountSelectionModal,
} from '@/components/features/donation';
import type { DonationType } from '@/types/donation.types';

export function DonatePage() {
  const { companyIdNumber } = useCompany();
  const { addItem, clearCartAndSetPurpose, scrollToCart } = useCart();

  const [donationTypes, setDonationTypes] = useState<DonationType[]>([]);
  const [selectedDonation, setSelectedDonation] = useState<DonationType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Clear cart and set purpose to donation (1) on component mount
  useEffect(() => {
    clearCartAndSetPurpose(1);
  }, [clearCartAndSetPurpose]);

  // Load donation types and services
  useEffect(() => {
    const loadData = async () => {
      if (!companyIdNumber) return;

      try {
        setIsLoading(true);
        setError(null);

        // Load donation types that should show on web
        const donationTypesData = await getDonationTypes(companyIdNumber);
        // Filter to only show web-enabled donations
        const webDonations = donationTypesData.items.filter((dt) => dt.showWeb === 1);
        setDonationTypes(webDonations);
      } catch (err) {
        console.error('Failed to load donation data:', err);
        setError(err instanceof Error ? err : new Error('Failed to load donation data'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [companyIdNumber]);

  const handleSelectDonation = (donation: DonationType) => {
    setSelectedDonation(donation);
    setIsModalOpen(true);
  };

  const handleAddToCart = (amount: number) => {
    if (!selectedDonation) return;

    addItem({
      donationType: selectedDonation,
      serviceType: undefined,
      amount,
      purposeId: 1, // Donation
    });

    // Scroll to cart on mobile after adding
    scrollToCart();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDonation(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading donation options..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-2/3">
          <ErrorMessage error={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Welcome Message */}
      <WelcomeMessage />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Area */}
        <div className="lg:col-span-2 bg-primary-100 p-2 rounded-xl shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Select the cause that you like to donate to
          </h2>
          {donationTypes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No donation types available at this time.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {donationTypes.map((donation) => (
                <DonationTypeCard
                  key={donation.donationType}
                  donationType={donation}
                  onSelect={handleSelectDonation}
                />
              ))}
            </div>
          )}
        </div>
        {/* Cart Sidebar */}
        <div className="lg:col-span-1">
          <Cart />
        </div>
      </div>

      {/* Amount Selection Modal */}
      {selectedDonation && (
        <AmountSelectionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          donationType={selectedDonation}
          onAddToCart={handleAddToCart}
        />
      )}
    </div>
  );
}
